
import React, { useState, useEffect, useCallback } from "react";
import "./LecturesPage.css";
import api from "../../api";
import AdminSectionHeader from "../../components/admin/AdminSectionHeader";
import StatusMessage from "../../components/admin/StatusMessage";
import LectureRow from "../../components/admin/LectureRow";

const LANGUAGES = [
  "English", "Hindi", "Bengali", "Marathi", "Tamil", "Telugu", "Kannada", "Gujarati", "Sanskrit"
];

function formatTime(seconds) {
  if (!seconds || seconds < 0) return "";
  if (seconds < 60) return `~${Math.round(seconds)}s remaining`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return `~${mins}m ${secs}s remaining`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `~${hours}h ${remainingMins}m remaining`;
}

export default function LecturesPage() {
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [newTopicForm, setNewTopicForm] = useState({ name: "", description: "", language: "English" });
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [lectures, setLectures] = useState([]);
  const [deleteTopicConfirm, setDeleteTopicConfirm] = useState(null);
  const [deleteTopicId, setDeleteTopicId] = useState(null);
  const [deleteSelectedConfirm, setDeleteSelectedConfirm] = useState(null);
  const [selectedLecturesForDelete, setSelectedLecturesForDelete] = useState(new Set());
  const initialUploadQueue = [
    {
      id: "row-0",
      lectureNumber: 1,
      title: "",
      videoFile: null,
      handoutFile: null,
      lectureDate: "",
      topicId: null
    }
  ];
  const [uploadQueue, setUploadQueue] = useState(initialUploadQueue);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadCurrentLecture, setUploadCurrentLecture] = useState("");
  const [uploadTotalLectures, setUploadTotalLectures] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadEstimatedRemaining, setUploadEstimatedRemaining] = useState(0);
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);

  const showMsg = (type, text, duration = 3000) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), duration);
  };

  useEffect(() => {
    let cancelled = false;
    setLoadingTopics(true);
    api
      .get("/api/lectures/topics")
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.topics || [];
        setTopics(list);
        if (list.length > 0) {
          setSelectedTopicId(list[0].id);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingTopics(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleNewTopicChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewTopicForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCreateTopic = useCallback(async () => {
    if (!newTopicForm.name.trim()) {
      showMsg("error", "Topic name is required");
      return;
    }
    setIsCreatingTopic(true);
    try {
      await api.post("/api/lectures/topics", newTopicForm);
      showMsg("success", "Topic created successfully");
      setNewTopicForm({ name: "", description: "", language: "English" });
      const res = await api.get("/api/lectures/topics");
      const list = res.data?.topics || [];
      setTopics(list);
      if (list.length > 0) {
        setSelectedTopicId(list[list.length - 1].id);
      }
    } catch (err) {
      showMsg("error", "Failed to create topic");
    } finally {
      setIsCreatingTopic(false);
    }
  }, [newTopicForm]);

  const handleUploadRowChange = useCallback((index, field, value) => {
    setUploadQueue((prev) => {
      const newQueue = [...prev];
      newQueue[index] = { ...newQueue[index], [field]: value };
      return newQueue;
    });
  }, []);

  const handleTopicSelect = useCallback((index, topicId) => {
    setUploadQueue((prev) => {
      const newQueue = [...prev];
      newQueue[index] = { ...newQueue[index], topicId };
      return newQueue;
    });
  }, []);

  const handleVideoFileChange = useCallback((index, file) => {
    handleUploadRowChange(index, "videoFile", file);
    handleUploadRowChange(index, "lectureNumber", index + 1);
  }, []);

  const handleHandoutFileChange = useCallback((index, file) => {
    handleUploadRowChange(index, "handoutFile", file);
  }, []);

  const fetchTopics = useCallback(async () => {
    try {
      const res = await api.get("/api/lectures/topics");
      const list = res.data?.topics || [];
      setTopics(list);
      if (list.length > 0 && !list.some((topic) => String(topic.id) === String(selectedTopicId))) {
        setSelectedTopicId(list[0].id);
      }
      return list;
    } catch (err) {
      console.error('Failed to load topics:', err);
      return [];
    }
  }, [selectedTopicId]);

  const fetchLecturesForTopic = useCallback(async (topicId) => {
    if (!topicId) {
      setLectures([]);
      return;
    }

    try {
      const res = await api.get(`/api/lectures/by-topic/${topicId}`);
      const lectures = res.data?.lectures || [];
      const sortedLectures = [...lectures].sort((a, b) => {
        const dateA = a.lectureDate ? new Date(a.lectureDate).getTime() : 0;
        const dateB = b.lectureDate ? new Date(b.lectureDate).getTime() : 0;
        return dateA - dateB;
      });
      setLectures(sortedLectures);
    } catch (err) {
      console.error('Failed to load lectures:', err);
    }
  }, []);

  // Fetch lectures for selected topic
  useEffect(() => {
    if (!selectedTopicId) {
      setLectures([]);
      return;
    }

    let cancelled = false;
    const refreshLectures = async () => {
      try {
        const res = await api.get(`/api/lectures/by-topic/${selectedTopicId}`);
        if (!cancelled) {
          const lectures = res.data?.lectures || [];
          const sortedLectures = [...lectures].sort((a, b) => {
            const dateA = a.lectureDate ? new Date(a.lectureDate).getTime() : 0;
            const dateB = b.lectureDate ? new Date(b.lectureDate).getTime() : 0;
            return dateA - dateB;
          });
          setLectures(sortedLectures);
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to load lectures:', err);
      }
    };

    refreshLectures();
    const intervalId = setInterval(() => {
      refreshLectures();
      fetchTopics();
    }, 10000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedTopicId, fetchTopics]);

  // Bulk lecture deletion handlers
  const handleToggleLectureSelection = useCallback((lectureId) => {
    setSelectedLecturesForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lectureId)) {
        newSet.delete(lectureId);
      } else {
        newSet.add(lectureId);
      }
      return newSet;
    });
  }, []);

  const handleDeleteSelectedLectures = useCallback(() => {
    if (selectedLecturesForDelete.size === 0) return;
    setDeleteSelectedConfirm(true);
  }, [selectedLecturesForDelete]);

  const handleConfirmDeleteSelected = useCallback(async () => {
    if (selectedLecturesForDelete.size === 0) return;
    try {
      const ids = Array.from(selectedLecturesForDelete);
      for (const id of ids) {
        await api.delete(`/api/lectures/${id}`);
      }
      showMsg("success", `Successfully deleted ${ids.length} lecture(s)`);
      setLectures(prev => prev.filter(l => !selectedLecturesForDelete.has(l.id)));
      // Refresh topics to update lecture count
      const res = await api.get("/api/lectures/topics");
      setTopics(res.data?.topics || []);
      setSelectedLecturesForDelete(new Set());
      setDeleteSelectedConfirm(null);
    } catch (err) {
      showMsg("error", "Failed to delete selected lectures");
    }
  }, [selectedLecturesForDelete]);

  const handleCancelDeleteSelected = useCallback(() => {
    setDeleteSelectedConfirm(null);
  }, []);

  const handleDeleteTopic = useCallback((topicId) => {
    setDeleteTopicId(topicId);
    setDeleteTopicConfirm(true);
  }, []);

  const handleConfirmDeleteTopic = useCallback(async () => {
    if (!deleteTopicId) return;
    try {
      // First delete all lectures associated with this topic
      const lecturesRes = await api.get(`/api/lectures/by-topic/${deleteTopicId}`);
      const topicLectures = lecturesRes.data?.lectures || [];
      
      // Delete each lecture
      for (const lecture of topicLectures) {
        await api.delete(`/api/lectures/${lecture.id}`);
      }
      
      // Now delete the topic itself
      await api.delete(`/api/lectures/topics/${deleteTopicId}`);
      showMsg("success", "Topic and all associated lectures deleted successfully");
      
      // Refresh topics list
      const res = await api.get("/api/lectures/topics");
      const list = res.data?.topics || [];
      setTopics(list);
      if (list.length > 0) {
        setSelectedTopicId(list[list.length - 1].id);
      } else {
        setSelectedTopicId(null);
      }
      if (list.length > 0) {
        await fetchLecturesForTopic(list[list.length - 1].id);
      } else {
        setLectures([]);
      }
      
      setDeleteTopicConfirm(null);
      setDeleteTopicId(null);
    } catch (err) {
      showMsg("error", "Failed to delete topic and associated lectures");
    }
  }, [deleteTopicId]);

  const handleCancelDeleteTopic = useCallback(() => {
    setDeleteTopicConfirm(null);
    setDeleteTopicId(null);
  }, []);

  const handleDeleteRow = useCallback((index) => {
    setUploadQueue((prev) => {
      const newQueue = [...prev];
      newQueue[index] = {
        id: "row-" + index,
        lectureNumber: index + 1,
        title: "",
        videoFile: null,
        handoutFile: null,
        lectureDate: "",
        topicId: null
      };
      return newQueue;
    });
  }, []);

  const handleUpload = useCallback(async () => {
    const validRows = uploadQueue.filter((row) => row?.title?.trim() && row?.videoFile && row?.lectureDate && row?.topicId);
    if (validRows.length === 0) {
      showMsg("error", "Please fill in at least one lecture with title, video, date, and topic");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadCurrentLecture("");
    setUploadTotalLectures(validRows.length);
    setUploadedCount(0);
    setUploadEstimatedRemaining(0);

    const totalBytes = validRows.reduce((sum, row) => {
      const rowBytes = (row.videoFile?.size || 0) + (row.handoutFile?.size || 0);
      return sum + rowBytes;
    }, 0);
    const uploadStartedAt = Date.now();

    try {
      let count = 0;
      let uploadedBytes = 0;

      for (const row of validRows) {
        setUploadCurrentLecture(row.title || `Lecture ${row.lectureNumber}`);
        const rowBytes = (row.videoFile?.size || 0) + (row.handoutFile?.size || 0);
        const formData = new FormData();
        formData.append("topic_id", row.topicId);
        formData.append("lecture_number", row.lectureNumber.toString());
        formData.append("title", row.title);
        formData.append("lecture_date", row.lectureDate);
        formData.append("video_file", row.videoFile);
        if (row.handoutFile) {
          formData.append("handout_file", row.handoutFile);
        }

        await api.post("/api/lectures/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            if (!progressEvent || !Number.isFinite(progressEvent.loaded)) {
              return;
            }

            const total = Number(progressEvent.total) || rowBytes || 1;
            const rowLoaded = Math.min(Number(progressEvent.loaded) || 0, total);
            const cumulativeLoaded = uploadedBytes + rowLoaded;
            const nextProgress = totalBytes > 0
              ? Math.min(100, Math.round((cumulativeLoaded / totalBytes) * 100))
              : 0;

            setUploadProgress(nextProgress);

            if (totalBytes > 0) {
              const elapsedSeconds = Math.max((Date.now() - uploadStartedAt) / 1000, 0.1);
              const remainingBytes = Math.max(0, totalBytes - cumulativeLoaded);
              const transferRate = rowLoaded / elapsedSeconds;
              setUploadEstimatedRemaining(transferRate > 0 ? remainingBytes / transferRate : 0);
            }
          }
        });

        uploadedBytes += rowBytes;
        count += 1;
        setUploadedCount(count);
      }

      showMsg("success", "Successfully uploaded " + validRows.length + " lecture(s)");
      // Log thumbnail info if available (we'd need to capture from responses)
      // For now, we can just note that thumbnails should be generated
      console.log("Thumbnails generated for uploaded videos (check server logs)");
      setUploadQueue(initialUploadQueue);
      if (selectedTopicId) {
        const refreshedTopics = await api.get("/api/lectures/topics");
        setTopics(refreshedTopics.data?.topics || []);
        await fetchLecturesForTopic(selectedTopicId);
      }
    } catch (err) {
      showMsg("error", err?.response?.data?.detail || "Upload failed");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadCurrentLecture("");
      setUploadTotalLectures(0);
      setUploadedCount(0);
      setUploadEstimatedRemaining(0);
    }
  }, [uploadQueue, initialUploadQueue, selectedTopicId, fetchLecturesForTopic]);

  return (
    <div className="admin-lectures-page">
      <AdminSectionHeader title="Lecture Uploads" description="Upload video lectures organized by topic and language" />
      <StatusMessage type={msg.type} message={msg.text} onClose={() => setMsg({ type: "", text: "" })} />
      <section className="topic-manager-section"><h3>Topic Manager</h3>
        {loadingTopics ? (
          <div className="skeleton skeleton-row" style={{ height: "3rem", width: "60%" }} />
        ) : (
          <div className="topic-dropdown-container">
            <select
              className="topic-dropdown"
              value={selectedTopicId || ""}
              onChange={(e) => setSelectedTopicId(e.target.value)}
              aria-label="Select topic"
            >
              <option value="">None</option>
              {topics.length === 0 ? (
                <option value="">No topics available</option>
              ) : (
                topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name} ({topic.language || "English"})
                  </option>
                ))
              )}
            </select>
          </div>
        )}
        {topics.length > 0 && (
          <div className="topic-actions">
            {topics.map((topic) => (
              <div key={topic.id} className="topic-item">
                <div className="topic-name">
                  {topic.name} ({topic.language || "English"})
                </div>
                <button
                  type="button"
                  className="btn-sm"
                  onClick={() => handleDeleteTopic(topic.id)}
                  aria-label={`Delete topic: ${topic.name}`}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
        {deleteTopicConfirm && deleteTopicId && (
          <div className="delete-topic-confirm">
            <span className="topic-name">
              Are you sure you want to delete this topic? All associated lectures will be permanently deleted.
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancelDeleteTopic}
              style={{ padding: "0.375rem 0.75rem", fontSize: "0.8rem" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleConfirmDeleteTopic}
              style={{ padding: "0.375rem 0.75rem", fontSize: "0.8rem" }}
            >
              Delete
            </button>
          </div>
        )}
        <div className="new-topic-form-container"><h4>Add New Topic</h4>
          <div className="new-topic-form">
            <input
              type="text"
              name="name"
              placeholder="Topic Name"
              value={newTopicForm.name}
              onChange={handleNewTopicChange}
              className="form-input"
              aria-label="Topic Name"
              required
            />
            <textarea
              name="description"
              placeholder="Description"
              value={newTopicForm.description}
              onChange={handleNewTopicChange}
              className="form-textarea"
              rows={2}
              aria-label="Description"
            />
            <select
              name="language"
              value={newTopicForm.language}
              onChange={handleNewTopicChange}
              className="form-select"
              aria-label="Language"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreateTopic}
              disabled={!newTopicForm.name.trim() || isCreatingTopic}
            >
              {isCreatingTopic ? "Creating..." : "Create Topic"}
            </button>
          </div>
        </div>
      </section>
      <section className="lecture-uploader-section"><h3>Lecture Uploader</h3>
        <p className="uploader-description">
          Maximum video file size: 2GB. Maximum handout file size: 10MB. Please fill in all required fields.
        </p>
        <div className="upload-queue">
          {uploadQueue.map((row, index) => (
            <LectureRow
              key={row.id}
              rowIndex={index}
              lectureNumber={row.lectureNumber}
              title={row.title}
              lectureDate={row.lectureDate}
              onLectureDateChange={(date) => handleUploadRowChange(index, "lectureDate", date)}
              onTitleChange={(title) => handleUploadRowChange(index, "title", title)}
              onVideoChange={(file) => handleVideoFileChange(index, file)}
              onHandoutChange={(file) => handleHandoutFileChange(index, file)}
              onDelete={() => handleDeleteRow(index)}
              topics={topics}
              selectedTopicId={row.topicId}
              onTopicSelect={handleTopicSelect}
            />
          ))}
        </div>
        <div className="upload-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={isUploading || !uploadQueue.some((row) => row?.title?.trim() && row?.videoFile && row?.lectureDate && row?.topicId)}
          >
            {isUploading ? `Uploading... (${uploadedCount}/${uploadTotalLectures})` : "Upload Selected Lectures"}
          </button>
          {isUploading && (
            <div className="upload-progress">
              <div className="progress-bar-container">
                <div 
                  className="progress-bar" 
                  style={{ width: `${uploadProgress}%` }}
                  role="progressbar"
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <span className="upload-text">
                {uploadCurrentLecture} ({uploadProgress}%) — {formatTime(uploadEstimatedRemaining)}
              </span>
            </div>
          )}
        </div>
      </section>
      <section className="lecture-deletion-manager-section"><h3>Lecture Deletion Manager</h3>
        <p className="manager-description">
          Select a topic and choose lectures to delete from the database and Azure storage.
        </p>
        <div className="topic-selection-dropdown">
          <label htmlFor="bulk-delete-topic-select">Select Topic</label>
          <select
            id="bulk-delete-topic-select"
            value={selectedTopicId || ""}
            onChange={(e) => {
              setSelectedTopicId(e.target.value);
              setSelectedLecturesForDelete(new Set());
            }}
            aria-label="Select topic for bulk deletion"
          >
            <option value="">-- Select Topic --</option>
            {topics.length === 0 ? (
              <option value="" disabled>No topics available</option>
            ) : (
              topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.name} ({topic.language || "English"})
                </option>
              ))
            )}
          </select>
        </div>
        {selectedTopicId && lectures.length > 0 && (
          <>
            <div className="lecture-deletion-list">
              {lectures.map((lecture) => (
                <div key={lecture.id} className="lecture-deletion-item">
                  <div className="lecture-deletion-info">
                    <span className="lecture-deletion-number">#{lecture.lectureNumber}</span>
                    <span className="lecture-deletion-title">{lecture.title}</span>
                    {lecture.description && (
                      <span className="lecture-deletion-desc">{lecture.description}</span>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    className="lecture-deletion-checkbox"
                    checked={selectedLecturesForDelete.has(lecture.id)}
                    onChange={() => handleToggleLectureSelection(lecture.id)}
                    aria-label={`Select lecture ${lecture.lectureNumber}: ${lecture.title}`}
                  />
                </div>
              ))}
            </div>
            <div className="lecture-deletion-actions">
              <button
                type="button"
                className="delete-selected-btn"
                onClick={handleDeleteSelectedLectures}
                disabled={selectedLecturesForDelete.size === 0}
              >
                Delete Selected ({selectedLecturesForDelete.size})
              </button>
            </div>
          </>
        )}
        {selectedTopicId && lectures.length === 0 && (
          <div className="lecture-list-empty">
            <p>No lectures uploaded for this topic yet.</p>
          </div>
        )}
        {deleteSelectedConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-modal">
              <h4>Confirm Bulk Delete</h4>
              <div className="delete-confirm-text">
                <p>Are you sure you want to delete the selected lectures?</p>
                <p>This action cannot be undone. All selected lectures will be permanently deleted from the database and Azure storage.</p>
              </div>
              <div className="delete-confirm-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancelDeleteSelected}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDeleteSelected}>
                  Delete {selectedLecturesForDelete.size} Lecture(s)
                </button>
              </div>
            </div>
          </div>
        )}
            </section>
    </div>
  );
}
